import axios from "axios";
import { refreshTokenFunction } from "../../context/AuthContext";

/**
 * Normalizes endpoint names to handle different naming conventions
 * @param {string} endPoint - The original endpoint name
 * @returns {string} - The normalized endpoint name
 */
const normalizeEndpoint = (endPoint) => {
  // Normalize endpoint names for consistent handling
  const normalized = endPoint.toLowerCase().trim();
  
  // Map of alternative spellings/formats to canonical endpoint names
  const endpointMap = {
    'institutional_table_data': 'institutional_table_data',
    'table_data': 'institutional_table_data',
    'institutional_treemap_data': 'institutional_treemap_data',
    'treemap_data': 'institutional_treemap_data',
    'treemap': 'institutional_treemap_data'
  };
  
  return endpointMap[normalized] || endPoint;
};

/**
 * Validates if an endpoint should never have query parameters
 * @param {string} endpoint - The endpoint to check
 * @returns {boolean} - Whether the endpoint should avoid parameters
 */
const shouldAvoidParams = (endpoint) => {
  const normalized = normalizeEndpoint(endpoint);
  
  // List of endpoints that should NOT receive query parameters
  const noParamEndpoints = [
    'institutional_treemap_data'
  ];
  
  return noParamEndpoints.includes(normalized);
};

/**
 * Determines if an endpoint requires special handling
 * @param {string} endpoint - The endpoint to check
 * @returns {Object} - Info about special handling requirements
 */
const getEndpointInfo = (endpoint) => {
  const normalized = normalizeEndpoint(endpoint);
  
  // Define endpoint-specific configurations
  const endpointConfig = {
    'institutional_table_data': {
      allowsParams: true,
      defaultParams: {
        page_size: 500,  // Request more records by default to get comprehensive data
        page: 1
      },
      dataPath: 'data',  // Where to find the data array in the response
      systemsPath: 'cleaned_idn_list',  // Where to find the systems list
      requiresPagination: true
    },
    'institutional_treemap_data': {
      allowsParams: false,  // Do NOT send parameters to this endpoint
      dataPath: 'data',
      requiresPagination: false
    }
  };
  
  return endpointConfig[normalized] || { allowsParams: true, requiresPagination: false };
};

/**
 * Formats an error message for logging
 * @param {Error} error - The error object
 * @returns {string} - Formatted error message
 */
const formatErrorMessage = (error) => {
  let message = `Error: ${error.message || 'Unknown error'}`;
  
  if (error.response) {
    message += `\nStatus: ${error.response.status}`;
    message += `\nStatus Text: ${error.response.statusText}`;
    
    if (error.response.data) {
      if (typeof error.response.data === 'string') {
        message += `\nResponse: ${error.response.data.substring(0, 100)}...`;
      } else {
        message += `\nResponse: ${JSON.stringify(error.response.data).substring(0, 100)}...`;
      }
    }
  }
  
  if (error.config) {
    message += `\nURL: ${error.config.url}`;
    message += `\nMethod: ${error.config.method.toUpperCase()}`;
    
    if (error.config.params) {
      message += `\nParams: ${JSON.stringify(error.config.params)}`;
    }
  }
  
  return message;
};

/**
 * Processes the response from an API to handle different response structures
 * @param {Object} responseData - The API response data
 * @param {Object} endpointInfo - Configuration for the endpoint
 * @returns {Object} - Processed response data
 */
const processApiResponse = (responseData, endpointInfo) => {
  if (!responseData) {
    console.warn('Empty response data');
    return { success: false, data: null };
  }
  
  // Check for numeric keys (raw database records)
  const numericKeys = Object.keys(responseData).filter(key => !isNaN(parseInt(key)));
  if (numericKeys.length > 0) {
    console.log(`Found ${numericKeys.length} numeric keys in response`);
    
    // Attempt to extract and process the data
    const processed = {
      success: true,
      data: [],
      hasNumericKeys: true,
      numericKeysCount: numericKeys.length
    };
    
    // If we're just looking for sample data, extract a few records
    const sampleKeys = numericKeys.slice(0, 5);
    sampleKeys.forEach(key => {
      try {
        // Some responses have JSON strings in the values
        if (typeof responseData[key] === 'string') {
          try {
            const parsedValue = JSON.parse(responseData[key]);
            processed.data.push(parsedValue);
          } catch (e) {
            // Not valid JSON, just use the string
            processed.data.push({ rawValue: responseData[key] });
          }
        } else {
          processed.data.push(responseData[key]);
        }
      } catch (e) {
        console.warn(`Could not process numeric key ${key}:`, e.message);
      }
    });
    
    return processed;
  }
  
  // Check for standard response structure
  if (endpointInfo?.dataPath && responseData[endpointInfo.dataPath]) {
    return {
      success: true,
      data: responseData[endpointInfo.dataPath],
      pagination: endpointInfo.requiresPagination ? {
        total: responseData.total || responseData[endpointInfo.dataPath].length,
        page: responseData.page || 1,
        pageSize: responseData.page_size || 10,
        totalPages: responseData.total 
          ? Math.ceil(responseData.total / (responseData.page_size || 10))
          : 1
      } : null,
      // Include any systems list if available
      systems: endpointInfo.systemsPath && responseData[endpointInfo.systemsPath] 
        ? responseData[endpointInfo.systemsPath] 
        : []
    };
  }
  
  // Just return the raw response if we can't process it
  return {
    success: true,
    data: responseData,
    isRawResponse: true
  };
};

/**
 * Fetches data from the API with error handling and token refresh
 * @param {string} endPoint - The API endpoint to fetch data from
 * @param {string} token - The access token
 * @param {string} refreshToken - The refresh token
 * @param {Object} params - Optional query parameters
 * @param {boolean} rawResponse - Whether to return the raw response without parsing JSON
 * @returns {Promise} - The data from the API
 */
export async function getDataStats(endPoint, token, refreshToken, params = {}, rawResponse = false) {
  // Normalize the endpoint name
  const normalizedEndpoint = normalizeEndpoint(endPoint);
  console.log(`Request to API endpoint: ${normalizedEndpoint}${rawResponse ? ' (raw response mode)' : ''}`);
  
  // Get endpoint configuration
  const endpointInfo = getEndpointInfo(normalizedEndpoint);
  console.log(`Endpoint config:`, JSON.stringify(endpointInfo));
  
  // Determine if we should use parameters and which ones
  let requestParams = {};
  if (endpointInfo.allowsParams !== false) {
    // Merge default params with provided params
    requestParams = { 
      ...(endpointInfo.defaultParams || {}), 
      ...params 
    };
    console.log(`Using parameters:`, JSON.stringify(requestParams));
  } else {
    console.log(`No parameters for this endpoint (${normalizedEndpoint})`);
  }
  
  return new Promise(function(resolve, reject) {
    // Prepare the request configuration
    const requestConfig = {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      // Set a reasonable timeout
      timeout: 30000,
      // Option to get raw response
      transformResponse: rawResponse ? [(data) => data] : undefined
    };
    
    // Only add params if the endpoint supports them
    if (Object.keys(requestParams).length > 0 && endpointInfo.allowsParams !== false) {
      requestConfig.params = requestParams;
    }
    
    // Start timestamp for performance monitoring
    const startTime = Date.now();
    
    console.log(`Making request to ${normalizedEndpoint} (${new Date().toISOString()})`);
    
    axios
      .get(`https://clinica-server.replit.app/${normalizedEndpoint}`, requestConfig)
      .then(async function (response) {
        const endTime = Date.now();
        console.log(`Response received in ${endTime - startTime}ms`);
        
        // handle success
        if (response.data) {
          console.log(`Response status: ${response.status}`);
          
          // For raw response mode, just return the data as is
          if (rawResponse) {
            console.log(`Returning raw response (typeof: ${typeof response.data})`);
            if (typeof response.data === 'string') {
              console.log(`Raw response length: ${response.data.length} characters`);
              console.log(`First 100 chars: ${response.data.substring(0, 100)}...`);
            }
            resolve(response.data);
            return;
          }
          
          // Log response structure
          if (typeof response.data === 'object') {
            const responseKeys = Object.keys(response.data);
            console.log(`Response keys (${responseKeys.length}): ${responseKeys.slice(0, 10).join(', ')}${responseKeys.length > 10 ? '...' : ''}`);
            
            // Process the response data
            const processed = processApiResponse(response.data, endpointInfo);
            
            if (processed.hasNumericKeys) {
              console.log(`Response has numeric keys structure. Extracted ${processed.data.length} sample records.`);
              // Return the raw response along with our processed data
              resolve({
                data: response.data,
                processed: processed
              });
            } else {
              console.log(`Standard response structure. Data array length: ${processed.data?.length || 0}`);
              resolve(response.data);
            }
          } else {
            // Handle string or other non-object response types
            console.log(`Non-object response type: ${typeof response.data}`);
            resolve(response.data);
          }
        } else {
          console.warn('Response received but no data property');
          
          // Token refresh logic
          if (response.status === 401) {
            console.log('Unauthorized. Attempting token refresh...');
            try {
              const refreshedToken = await refreshTokenFunction(refreshToken);
              if (refreshedToken) {
                return getDataStats(normalizedEndpoint, refreshedToken, refreshToken, params, rawResponse);
              } else {
                console.error("Failed to refresh token");
                reject({
                  message: "Authentication error",
                  authError: true
                });
              }
            } catch (refreshError) {
              console.error("Error during token refresh:", refreshError);
              reject({
                message: "Authentication refresh error",
                authError: true,
                originalError: refreshError
              });
            }
          } else {
            console.error("Failed to fetch data:", response.statusText);
            reject({
              message: "Data fetch error: " + response.statusText,
              response: response
            });
          }
        }
      })
      .catch(async function(error) {
        console.error(formatErrorMessage(error));
        
        // Special handling for endpoints that might fail due to parameters
        if (error.response && error.response.status === 500 && 
            Object.keys(requestParams).length > 0 && 
            normalizedEndpoint === 'institutional_treemap_data') {
            
          console.log(`Received 500 error with parameters. Retrying ${normalizedEndpoint} without parameters...`);
          
          // Retry without parameters
          try {
            const retryResponse = await axios.get(`https://clinica-server.replit.app/${normalizedEndpoint}`, {
              headers: {
                Authorization: `Bearer ${token}`,
              },
              timeout: 30000
            });
            
            console.log(`Retry successful for ${normalizedEndpoint} without parameters`);
            resolve(retryResponse.data);
          } catch (retryError) {
            console.error(`Retry also failed for ${normalizedEndpoint}:`, formatErrorMessage(retryError));
            reject({
              message: "API retry failed",
              originalError: error,
              retryError: retryError
            });
          }
        } else if (error.response && error.response.status === 401) {
          // Try token refresh on 401
          console.log('Unauthorized. Attempting token refresh...');
          try {
            const refreshedToken = await refreshTokenFunction(refreshToken);
            if (refreshedToken) {
              return getDataStats(normalizedEndpoint, refreshedToken, refreshToken, params, rawResponse);
            } else {
              reject({
                message: "Authentication error",
                authError: true,
                originalError: error
              });
            }
          } catch (refreshError) {
            reject({
              message: "Authentication refresh error",
              authError: true,
              originalError: error,
              refreshError: refreshError
            });
          }
        } else {
          reject({
            message: "API request failed",
            originalError: error
          });
        }
      });
  });
}
